/**
 * WindParticleLayer
 *
 * Mapbox GL JS CustomLayer — GPU-accelerated wind particle animation.
 *
 * Rendering pipeline each frame:
 * 1. UPDATE   — transform feedback moves particles (stores prev position for lines)
 * 2. FADE     — draw previous trail texture at reduced opacity (trail decay)
 * 3. DRAW     — render instanced line-segment quads into the trail FBO
 * 4. HEATMAP  — render semi-transparent wind speed color field onto the map
 * 5. SCREEN   — composite the trail texture onto the map (with globe mask)
 *
 * Particles are rendered as screen-space line segments (prev → current position)
 * expanded into quads with soft alpha edges, creating flowing brushstroke trails.
 */

import { PARTICLE_CONFIG, detectGPUTier, generateColorRampData, getAdaptiveParticleCount } from '@/config/windConfig';
import { useWindStore } from '@/stores/slices/useWindStore';
import type { GPUTier, GeoBounds, WindParticleConfig } from '@/types/wind';
import { WindDataLoader } from './WindDataLoader';
import {
  DRAW_FRAG,
  DRAW_VERT,
  FADE_FRAG,
  FADE_VERT,
  HEATMAP_FRAG,
  HEATMAP_VERT,
  SCREEN_FRAG,
  SCREEN_VERT,
  UPDATE_FRAG,
  UPDATE_VERT,
} from './windShaders';

const GLOBAL_BOUNDS: GeoBounds = { west: -180, south: -90, east: 180, north: 90 };

interface MapboxCustomLayerInterface {
  id: string;
  type: 'custom';
  renderingMode?: '2d' | '3d';
  onAdd?(map: mapboxgl.Map, gl: WebGL2RenderingContext): void;
  render(gl: WebGL2RenderingContext, matrix: number[]): void;
  onRemove?(map: mapboxgl.Map, gl: WebGL2RenderingContext): void;
}

export class WindParticleLayer implements MapboxCustomLayerInterface {
  readonly id: string;
  readonly type = 'custom' as const;
  readonly renderingMode = '3d' as const;

  private map: mapboxgl.Map | null = null;
  private gl: WebGL2RenderingContext | null = null;

  // Shader programs
  private updateProgram: WebGLProgram | null = null;
  private drawProgram: WebGLProgram | null = null;
  private fadeProgram: WebGLProgram | null = null;
  private screenProgram: WebGLProgram | null = null;
  private heatmapProgram: WebGLProgram | null = null;

  // Transform feedback
  private transformFeedback: WebGLTransformFeedback | null = null;

  // Particle buffers (ping-pong) — 6 floats per particle: pos(2), prev(2), age(1), speed(1)
  private particleBuffers: [WebGLBuffer | null, WebGLBuffer | null] = [null, null];
  private updateVAOs: [WebGLVertexArrayObject | null, WebGLVertexArrayObject | null] = [null, null];
  private drawVAOs: [WebGLVertexArrayObject | null, WebGLVertexArrayObject | null] = [null, null];
  private currentBuffer = 0;

  // Line quad template for instanced rendering
  private lineQuadBuffer: WebGLBuffer | null = null;

  // Trail FBO (ping-pong)
  private trailFramebuffer: WebGLFramebuffer | null = null;
  private trailTextures: [WebGLTexture | null, WebGLTexture | null] = [null, null];
  private currentTrail = 0;
  private trailWidth = 0;
  private trailHeight = 0;

  // Full-screen quad for fade/screen passes
  private quadVAO: WebGLVertexArrayObject | null = null;
  private quadBuffer: WebGLBuffer | null = null;

  // Heatmap mesh (subdivided grid for globe curvature)
  private heatmapVAO: WebGLVertexArrayObject | null = null;
  private heatmapVertexBuffer: WebGLBuffer | null = null;
  private heatmapIndexBuffer: WebGLBuffer | null = null;
  private heatmapIndexCount = 0;

  // Textures
  private windTexture: WebGLTexture | null = null;
  private colorRampTexture: WebGLTexture | null = null;

  // State
  private dataLoader = new WindDataLoader();
  private config: WindParticleConfig;
  private gpuTier: GPUTier = 'medium';
  private frameCount = 0;
  private fpsAccumulator = 0;
  private lastFrameTime = 0;
  private hasData = false;

  private currentUMin = -50;
  private currentUMax = 50;
  private currentVMin = -50;
  private currentVMax = 50;
  private currentMaxSpeed = 30.0;
  private currentBounds: GeoBounds = GLOBAL_BOUNDS;
  private currentIsGlobal = true;
  private currentSourceName = '';
  private currentMetadata: import('@/types/wind').WindMetadata | null = null;

  private moveEndHandler: (() => void) | null = null;
  private moveStartHandler: (() => void) | null = null;
  private dataRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private isLoadingData = false;
  private isMapMoving = false;
  private lastDataFetchTime = 0;
  private currentParticleCount = 0;
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(layerId: string) {
    this.id = layerId;
    this.config = PARTICLE_CONFIG.medium;
  }

  onAdd(map: mapboxgl.Map, gl: WebGL2RenderingContext): void {
    this.map = map;
    this.gl = gl;

    this.gpuTier = detectGPUTier(gl);
    this.config = PARTICLE_CONFIG[this.gpuTier];
    const canvas = gl.canvas as HTMLCanvasElement;
    this.currentParticleCount = getAdaptiveParticleCount(this.gpuTier, map.getZoom(), canvas.width, canvas.height);
    console.log(`WindParticleLayer: GPU tier=${this.gpuTier}, particles=${this.currentParticleCount} (base ${this.config.particleCount})`);
    useWindStore.getState().setWindState({ gpuTier: this.gpuTier });

    // Compile all shader programs
    this.updateProgram = this.createProgram(gl, UPDATE_VERT, UPDATE_FRAG, [
      'v_position',
      'v_prevPosition',
      'v_age',
      'v_speed',
    ]);
    this.drawProgram = this.createProgram(gl, DRAW_VERT, DRAW_FRAG);
    this.fadeProgram = this.createProgram(gl, FADE_VERT, FADE_FRAG);
    this.screenProgram = this.createProgram(gl, SCREEN_VERT, SCREEN_FRAG);
    this.heatmapProgram = this.createProgram(gl, HEATMAP_VERT, HEATMAP_FRAG);

    if (!this.updateProgram || !this.drawProgram || !this.fadeProgram || !this.screenProgram || !this.heatmapProgram) {
      console.error('WindParticleLayer: Failed to compile shaders');
      return;
    }

    this.transformFeedback = gl.createTransformFeedback();
    this.initLineQuadBuffer(gl);
    this.initParticleBuffers(gl);
    this.initQuadBuffer(gl);
    this.initHeatmapMesh(gl);
    this.colorRampTexture = this.createColorRampTexture(gl);

    this.loadWindData();
    this.dataRefreshTimer = setInterval(() => this.loadWindData(), 5 * 60 * 1000);

    this.moveStartHandler = () => {
      this.isMapMoving = true;
    };
    this.moveEndHandler = () => {
      this.isMapMoving = false;
      this.onViewportChange();
    };
    map.on('movestart', this.moveStartHandler);
    map.on('moveend', this.moveEndHandler);
  }

  render(gl: WebGL2RenderingContext, matrix: number[]): void {
    if (
      !this.hasData ||
      !this.updateProgram ||
      !this.drawProgram ||
      !this.fadeProgram ||
      !this.screenProgram ||
      !this.heatmapProgram
    ) {
      this.map?.triggerRepaint();
      return;
    }

    this.ensureTrailFBO(gl);
    this.trackFPS();

    // Save Mapbox GL state
    const mapboxFBO = gl.getParameter(gl.FRAMEBUFFER_BINDING);
    const depthTestWas = gl.isEnabled(gl.DEPTH_TEST);
    gl.disable(gl.DEPTH_TEST);

    const canvas = gl.canvas as HTMLCanvasElement;
    const zoom = this.map?.getZoom() ?? 2;
    const { lineWidth, speedFactor } = this.getZoomScale(zoom);

    // --- PASS 1: Update particle positions (transform feedback) ---
    this.updateParticles(gl, speedFactor);

    // --- PASS 2: Fade previous trail + Draw new particles into trail FBO ---
    const src = this.currentTrail;
    const dst = 1 - src;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.trailFramebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.trailTextures[dst], 0);
    gl.viewport(0, 0, this.trailWidth, this.trailHeight);

    // 2a: Fade previous trail
    gl.disable(gl.BLEND);
    gl.useProgram(this.fadeProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.trailTextures[src]);
    gl.uniform1i(gl.getUniformLocation(this.fadeProgram!, 'u_texture'), 0);
    // During map rotation/pan, aggressively fade trails to prevent screen-space ghosting
    const fadeOpacity = this.isMapMoving ? 0.8 : this.config.fadeOpacity;
    gl.uniform1f(gl.getUniformLocation(this.fadeProgram!, 'u_fadeOpacity'), fadeOpacity);
    gl.bindVertexArray(this.quadVAO);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);

    // 2b: Draw new particle line segments on top
    gl.enable(gl.BLEND);
    // Separate blend for alpha: prevents alpha squaring in the trail FBO
    gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.drawParticles(gl, matrix, lineWidth);
    gl.disable(gl.BLEND);

    // --- PASS 3: Restore Mapbox FBO, draw heatmap + composite trail ---
    gl.bindFramebuffer(gl.FRAMEBUFFER, mapboxFBO);
    gl.viewport(0, 0, canvas.width, canvas.height);

    // Compute globe circle once for both heatmap and screen passes
    const globe = this.computeGlobeCircle();

    // 3a: Draw heatmap background
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.drawHeatmap(gl, matrix, zoom, globe);

    // 3b: Composite trail texture on top (premultiplied alpha blend)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.screenProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.trailTextures[dst]);
    gl.uniform1i(gl.getUniformLocation(this.screenProgram!, 'u_texture'), 0);
    gl.uniform1f(gl.getUniformLocation(this.screenProgram!, 'u_opacity'), 1.0);

    gl.uniform2f(gl.getUniformLocation(this.screenProgram!, 'u_globeCenter'), globe.centerX, globe.centerY);
    gl.uniform1f(gl.getUniformLocation(this.screenProgram!, 'u_globeRadius'), globe.radius);
    gl.uniform2f(gl.getUniformLocation(this.screenProgram!, 'u_resolution'), canvas.width, canvas.height);
    gl.uniform1i(gl.getUniformLocation(this.screenProgram!, 'u_isGlobe'), globe.isGlobe ? 1 : 0);

    gl.bindVertexArray(this.quadVAO);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);

    gl.disable(gl.BLEND);
    // Restore standard blend func for Mapbox
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Restore Mapbox state
    if (depthTestWas) gl.enable(gl.DEPTH_TEST);

    // Swap ping-pong buffers
    this.currentTrail = dst;
    this.currentBuffer = 1 - this.currentBuffer;

    this.map?.triggerRepaint();
  }

  onRemove(_map: mapboxgl.Map, gl: WebGL2RenderingContext): void {
    if (this.map) {
      if (this.moveStartHandler) this.map.off('movestart', this.moveStartHandler);
      if (this.moveEndHandler) this.map.off('moveend', this.moveEndHandler);
      this.moveStartHandler = null;
      this.moveEndHandler = null;
    }

    if (this.updateProgram) gl.deleteProgram(this.updateProgram);
    if (this.drawProgram) gl.deleteProgram(this.drawProgram);
    if (this.fadeProgram) gl.deleteProgram(this.fadeProgram);
    if (this.screenProgram) gl.deleteProgram(this.screenProgram);
    if (this.heatmapProgram) gl.deleteProgram(this.heatmapProgram);
    if (this.transformFeedback) gl.deleteTransformFeedback(this.transformFeedback);
    if (this.trailFramebuffer) gl.deleteFramebuffer(this.trailFramebuffer);

    for (const buf of this.particleBuffers) if (buf) gl.deleteBuffer(buf);
    for (const vao of [...this.updateVAOs, ...this.drawVAOs]) if (vao) gl.deleteVertexArray(vao);
    for (const tex of this.trailTextures) if (tex) gl.deleteTexture(tex);

    if (this.lineQuadBuffer) gl.deleteBuffer(this.lineQuadBuffer);
    if (this.quadVAO) gl.deleteVertexArray(this.quadVAO);
    if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
    if (this.heatmapVAO) gl.deleteVertexArray(this.heatmapVAO);
    if (this.heatmapVertexBuffer) gl.deleteBuffer(this.heatmapVertexBuffer);
    if (this.heatmapIndexBuffer) gl.deleteBuffer(this.heatmapIndexBuffer);
    if (this.windTexture) gl.deleteTexture(this.windTexture);
    if (this.colorRampTexture) gl.deleteTexture(this.colorRampTexture);

    if (this.dataRefreshTimer) clearInterval(this.dataRefreshTimer);
    if (this.rebuildTimer) clearTimeout(this.rebuildTimer);
    this.dataLoader.dispose();
    this.map = null;
    this.gl = null;
  }

  // ─── Private: Initialization ───────────────────────────────────────

  private initLineQuadBuffer(gl: WebGL2RenderingContext): void {
    // Template quad for instanced line rendering: 4 corners as triangle strip
    // x: -1 = prev endpoint, +1 = current endpoint
    // y: -1 = left edge, +1 = right edge
    const quadData = new Float32Array([
      -1, -1, // start, left
      -1, 1, // start, right
      1, -1, // end, left
      1, 1, // end, right
    ]);
    this.lineQuadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.lineQuadBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, quadData, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  private initParticleBuffers(gl: WebGL2RenderingContext): void {
    for (const buf of this.particleBuffers) if (buf) gl.deleteBuffer(buf);
    for (const vao of [...this.updateVAOs, ...this.drawVAOs]) if (vao) gl.deleteVertexArray(vao);

    const count = this.currentParticleCount || this.config.particleCount;
    // 6 floats per particle: pos.xy, prevPos.xy, age, speed
    const data = new Float32Array(count * 6);
    for (let i = 0; i < count; i++) {
      const x = Math.random();
      const y = Math.random();
      data[i * 6 + 0] = x; // position.x
      data[i * 6 + 1] = y; // position.y
      data[i * 6 + 2] = x; // prevPosition.x (same = no initial trail)
      data[i * 6 + 3] = y; // prevPosition.y
      data[i * 6 + 4] = Math.random() * this.config.maxAge; // age
      data[i * 6 + 5] = 0; // speed
    }

    const STRIDE = 24; // 6 floats * 4 bytes

    for (let i = 0; i < 2; i++) {
      this.particleBuffers[i] = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffers[i]);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_COPY);

      // --- Update VAO (for transform feedback) ---
      this.updateVAOs[i] = gl.createVertexArray();
      gl.bindVertexArray(this.updateVAOs[i]);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffers[i]);

      const uPosLoc = gl.getAttribLocation(this.updateProgram!, 'a_position');
      const uPrevLoc = gl.getAttribLocation(this.updateProgram!, 'a_prevPosition');
      const uAgeLoc = gl.getAttribLocation(this.updateProgram!, 'a_age');
      const uSpdLoc = gl.getAttribLocation(this.updateProgram!, 'a_speed');

      gl.enableVertexAttribArray(uPosLoc);
      gl.vertexAttribPointer(uPosLoc, 2, gl.FLOAT, false, STRIDE, 0);
      gl.enableVertexAttribArray(uPrevLoc);
      gl.vertexAttribPointer(uPrevLoc, 2, gl.FLOAT, false, STRIDE, 8);
      gl.enableVertexAttribArray(uAgeLoc);
      gl.vertexAttribPointer(uAgeLoc, 1, gl.FLOAT, false, STRIDE, 16);
      gl.enableVertexAttribArray(uSpdLoc);
      gl.vertexAttribPointer(uSpdLoc, 1, gl.FLOAT, false, STRIDE, 20);

      gl.bindVertexArray(null);

      // --- Draw VAO (instanced: line quad template + particle data) ---
      this.drawVAOs[i] = gl.createVertexArray();
      gl.bindVertexArray(this.drawVAOs[i]);

      // Per-vertex: line quad template (divisor 0)
      gl.bindBuffer(gl.ARRAY_BUFFER, this.lineQuadBuffer);
      const quadLoc = gl.getAttribLocation(this.drawProgram!, 'a_quad');
      gl.enableVertexAttribArray(quadLoc);
      gl.vertexAttribPointer(quadLoc, 2, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(quadLoc, 0);

      // Per-instance: particle data (divisor 1)
      gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffers[i]);

      const dPosLoc = gl.getAttribLocation(this.drawProgram!, 'a_position');
      const dPrevLoc = gl.getAttribLocation(this.drawProgram!, 'a_prevPosition');
      const dAgeLoc = gl.getAttribLocation(this.drawProgram!, 'a_age');
      const dSpdLoc = gl.getAttribLocation(this.drawProgram!, 'a_speed');

      gl.enableVertexAttribArray(dPosLoc);
      gl.vertexAttribPointer(dPosLoc, 2, gl.FLOAT, false, STRIDE, 0);
      gl.vertexAttribDivisor(dPosLoc, 1);

      gl.enableVertexAttribArray(dPrevLoc);
      gl.vertexAttribPointer(dPrevLoc, 2, gl.FLOAT, false, STRIDE, 8);
      gl.vertexAttribDivisor(dPrevLoc, 1);

      gl.enableVertexAttribArray(dAgeLoc);
      gl.vertexAttribPointer(dAgeLoc, 1, gl.FLOAT, false, STRIDE, 16);
      gl.vertexAttribDivisor(dAgeLoc, 1);

      gl.enableVertexAttribArray(dSpdLoc);
      gl.vertexAttribPointer(dSpdLoc, 1, gl.FLOAT, false, STRIDE, 20);
      gl.vertexAttribDivisor(dSpdLoc, 1);

      gl.bindVertexArray(null);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  private initQuadBuffer(gl: WebGL2RenderingContext): void {
    const quadData = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);

    this.quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, quadData, gl.STATIC_DRAW);

    this.quadVAO = gl.createVertexArray();
    gl.bindVertexArray(this.quadVAO);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
    // Both fade and screen shaders use layout(location = 0) for a_pos
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  private initHeatmapMesh(gl: WebGL2RenderingContext): void {
    const cols = 48;
    const rows = 24;

    // Vertices: normalized [0,1] grid
    const vertices = new Float32Array((cols + 1) * (rows + 1) * 2);
    let vi = 0;
    for (let y = 0; y <= rows; y++) {
      for (let x = 0; x <= cols; x++) {
        vertices[vi++] = x / cols;
        vertices[vi++] = y / rows;
      }
    }

    // Indices: 2 triangles per cell
    const indices = new Uint16Array(cols * rows * 6);
    let ii = 0;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const tl = y * (cols + 1) + x;
        const tr = tl + 1;
        const bl = tl + (cols + 1);
        const br = bl + 1;
        indices[ii++] = tl;
        indices[ii++] = bl;
        indices[ii++] = tr;
        indices[ii++] = tr;
        indices[ii++] = bl;
        indices[ii++] = br;
      }
    }
    this.heatmapIndexCount = indices.length;

    this.heatmapVertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.heatmapVertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    this.heatmapIndexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.heatmapIndexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);

    this.heatmapVAO = gl.createVertexArray();
    gl.bindVertexArray(this.heatmapVAO);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.heatmapVertexBuffer);
    const posLoc = gl.getAttribLocation(this.heatmapProgram!, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.heatmapIndexBuffer);

    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  }

  private ensureTrailFBO(gl: WebGL2RenderingContext): void {
    const canvas = gl.canvas as HTMLCanvasElement;
    const w = canvas.width;
    const h = canvas.height;

    if (this.trailWidth === w && this.trailHeight === h && this.trailFramebuffer) return;

    console.log(`WindParticleLayer: Creating trail FBO ${w}x${h}`);

    if (this.trailFramebuffer) gl.deleteFramebuffer(this.trailFramebuffer);
    for (const tex of this.trailTextures) if (tex) gl.deleteTexture(tex);

    this.trailFramebuffer = gl.createFramebuffer();

    for (let i = 0; i < 2; i++) {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.trailTextures[i] = tex;
    }
    gl.bindTexture(gl.TEXTURE_2D, null);

    this.trailWidth = w;
    this.trailHeight = h;
    this.currentTrail = 0;
  }

  // ─── Private: Render Passes ────────────────────────────────────────

  private getViewportUV(): { min: [number, number]; max: [number, number] } {
    if (!this.map) return { min: [0, 0], max: [1, 1] };

    const mapBounds = this.map.getBounds();
    const b = this.currentBounds;
    const dataW = b.east - b.west;
    const dataH = b.north - b.south;

    if (dataW === 0 || dataH === 0) return { min: [0, 0], max: [1, 1] };

    // Map viewport geographic bounds to data texture UV [0,1] space
    let minX = (mapBounds.getWest() - b.west) / dataW;
    let maxX = (mapBounds.getEast() - b.west) / dataW;
    let minY = (b.north - mapBounds.getNorth()) / dataH;
    let maxY = (b.north - mapBounds.getSouth()) / dataH;

    // Add 20% padding so particles spawn slightly outside visible edges
    const padX = (maxX - minX) * 0.2;
    const padY = (maxY - minY) * 0.2;
    minX -= padX;
    maxX += padX;
    minY -= padY;
    maxY += padY;

    // Clamp to valid UV range
    minX = Math.max(0, minX);
    maxX = Math.min(1, maxX);
    minY = Math.max(0, minY);
    maxY = Math.min(1, maxY);

    return { min: [minX, minY], max: [maxX, maxY] };
  }

  private updateParticles(gl: WebGL2RenderingContext, speedFactor: number): void {
    const src = this.currentBuffer;
    const dst = 1 - src;

    gl.useProgram(this.updateProgram);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.windTexture);
    gl.uniform1i(gl.getUniformLocation(this.updateProgram!, 'u_windTexture'), 0);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_uMin'), this.currentUMin);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_uMax'), this.currentUMax);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_vMin'), this.currentVMin);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_vMax'), this.currentVMax);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_speedFactor'), speedFactor);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_maxAge'), this.config.maxAge);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram!, 'u_randomSeed'), Math.random());
    gl.uniform1i(gl.getUniformLocation(this.updateProgram!, 'u_isGlobal'), this.currentIsGlobal ? 1 : 0);

    // Viewport-constrained spawning: particles respawn within visible area
    const viewport = this.getViewportUV();
    gl.uniform2f(gl.getUniformLocation(this.updateProgram!, 'u_viewportMin'), viewport.min[0], viewport.min[1]);
    gl.uniform2f(gl.getUniformLocation(this.updateProgram!, 'u_viewportMax'), viewport.max[0], viewport.max[1]);

    gl.bindVertexArray(this.updateVAOs[src]);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.transformFeedback);
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.particleBuffers[dst]);

    gl.enable(gl.RASTERIZER_DISCARD);
    gl.beginTransformFeedback(gl.POINTS);
    gl.drawArrays(gl.POINTS, 0, this.currentParticleCount);
    gl.endTransformFeedback();
    gl.disable(gl.RASTERIZER_DISCARD);

    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
    gl.bindVertexArray(null);
  }

  private drawParticles(gl: WebGL2RenderingContext, matrix: number[], lineWidth: number): void {
    const dst = 1 - this.currentBuffer;

    gl.useProgram(this.drawProgram);

    gl.uniformMatrix4fv(gl.getUniformLocation(this.drawProgram!, 'u_matrix'), false, matrix);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_lineWidth'), lineWidth);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_maxAge'), this.config.maxAge);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_maxSpeed'), this.currentMaxSpeed);
    gl.uniform2f(gl.getUniformLocation(this.drawProgram!, 'u_resolution'), this.trailWidth, this.trailHeight);

    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_boundsWest'), this.currentBounds.west);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_boundsEast'), this.currentBounds.east);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_boundsNorth'), this.currentBounds.north);
    gl.uniform1f(gl.getUniformLocation(this.drawProgram!, 'u_boundsSouth'), this.currentBounds.south);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.colorRampTexture);
    gl.uniform1i(gl.getUniformLocation(this.drawProgram!, 'u_colorRamp'), 0);

    gl.bindVertexArray(this.drawVAOs[dst]);
    // Instanced rendering: 4 vertices per quad, currentParticleCount instances
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.currentParticleCount);
    gl.bindVertexArray(null);
  }

  private drawHeatmap(
    gl: WebGL2RenderingContext,
    matrix: number[],
    zoom: number,
    globe: { centerX: number; centerY: number; radius: number; isGlobe: boolean },
  ): void {
    if (!this.windTexture || !this.heatmapProgram) return;

    // Heatmap opacity: subtle at globe view, stronger when zoomed in
    const opacity = zoom < 3 ? 0.12 : zoom < 6 ? 0.18 : 0.25;

    gl.useProgram(this.heatmapProgram);

    gl.uniformMatrix4fv(gl.getUniformLocation(this.heatmapProgram!, 'u_matrix'), false, matrix);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_boundsWest'), this.currentBounds.west);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_boundsEast'), this.currentBounds.east);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_boundsNorth'), this.currentBounds.north);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_boundsSouth'), this.currentBounds.south);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.windTexture);
    gl.uniform1i(gl.getUniformLocation(this.heatmapProgram!, 'u_windTexture'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.colorRampTexture);
    gl.uniform1i(gl.getUniformLocation(this.heatmapProgram!, 'u_colorRamp'), 1);

    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_uMin'), this.currentUMin);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_uMax'), this.currentUMax);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_vMin'), this.currentVMin);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_vMax'), this.currentVMax);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_maxSpeed'), this.currentMaxSpeed);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_opacity'), opacity);

    // Globe circle mask — clip heatmap to visible globe
    gl.uniform2f(gl.getUniformLocation(this.heatmapProgram!, 'u_globeCenter'), globe.centerX, globe.centerY);
    gl.uniform1f(gl.getUniformLocation(this.heatmapProgram!, 'u_globeRadius'), globe.radius);
    gl.uniform2f(gl.getUniformLocation(this.heatmapProgram!, 'u_resolution'), (gl.canvas as HTMLCanvasElement).width, (gl.canvas as HTMLCanvasElement).height);
    gl.uniform1i(gl.getUniformLocation(this.heatmapProgram!, 'u_isGlobe'), globe.isGlobe ? 1 : 0);

    gl.bindVertexArray(this.heatmapVAO);
    gl.drawElements(gl.TRIANGLES, this.heatmapIndexCount, gl.UNSIGNED_SHORT, 0);
    gl.bindVertexArray(null);
  }

  // ─── Private: Globe & Zoom ────────────────────────────────────────

  private computeGlobeCircle(): { centerX: number; centerY: number; radius: number; isGlobe: boolean } {
    if (!this.map) return { centerX: 0, centerY: 0, radius: 0, isGlobe: false };

    const zoom = this.map.getZoom();
    if (zoom > 5.5) return { centerX: 0, centerY: 0, radius: 0, isGlobe: false };

    const dpr = window.devicePixelRatio || 1;

    try {
      const mapCenter = this.map.getCenter();
      const centerScreen = this.map.project(mapCenter);
      const edgeScreen = this.map.project({ lng: mapCenter.lng + 89, lat: mapCenter.lat });
      const radius = Math.hypot(edgeScreen.x - centerScreen.x, edgeScreen.y - centerScreen.y);
      return {
        centerX: centerScreen.x * dpr,
        centerY: centerScreen.y * dpr,
        radius: radius * dpr * 1.02,
        isGlobe: true,
      };
    } catch {
      const canvas = this.map.getCanvas();
      const minDim = Math.min(canvas.width, canvas.height);
      return { centerX: canvas.width / 2, centerY: canvas.height / 2, radius: minDim * 0.45, isGlobe: true };
    }
  }

  private getZoomScale(zoom: number): { lineWidth: number; speedFactor: number } {
    // At higher zoom: thicker lines
    const zoomFactor = Math.pow(2, Math.max(0, zoom - 2) * 0.3);

    // Speed: reduce at globe level (zoom < 3) AND at high zoom (zoom > 3)
    // This makes globe speed ≈ North America speed for consistent feel
    let speedScale: number;
    if (zoom < 3) {
      // Globe: 30% at zoom 0, ramps to 100% at zoom 3
      speedScale = 0.3 + (zoom / 3) * 0.7;
    } else {
      // Zoomed in: gradually reduce since map features are larger
      speedScale = 1.0 / Math.pow(2, (zoom - 3) * 0.5);
    }

    return {
      lineWidth: this.config.pointSize * Math.min(zoomFactor, 4.0),
      speedFactor: this.config.speedFactor * speedScale,
    };
  }

  // ─── Private: Data Loading ─────────────────────────────────────────

  private onViewportChange(): void {
    if (!this.map || !this.gl || this.isLoadingData) return;
    const mapBounds = this.map.getBounds();
    const viewportBounds: GeoBounds = {
      west: mapBounds.getWest(),
      south: mapBounds.getSouth(),
      east: mapBounds.getEast(),
      north: mapBounds.getNorth(),
    };
    const zoom = Math.floor(this.map.getZoom());
    const bestSource = this.dataLoader.selectSource(viewportBounds, zoom);

    // Trigger reload if source changed OR visible tiles changed
    const sourceChanged = bestSource.name !== this.currentSourceName;
    const tilesChanged = this.dataLoader.hasVisibleTilesChanged(
      bestSource,
      this.currentMetadata,
      viewportBounds,
      zoom,
    );

    if (sourceChanged || tilesChanged) {
      if (sourceChanged) {
        console.log(`WindParticleLayer: Switching from ${this.currentSourceName || 'none'} to ${bestSource.name}`);
      }
      this.loadWindData();
    }

    // Adaptive particle count: rebuild buffers if count changed significantly
    this.scheduleAdaptiveRebuild();
  }

  private scheduleAdaptiveRebuild(): void {
    if (!this.map || !this.gl) return;
    if (this.rebuildTimer) clearTimeout(this.rebuildTimer);

    this.rebuildTimer = setTimeout(() => {
      if (!this.map || !this.gl) return;
      const canvas = this.gl.canvas as HTMLCanvasElement;
      const newCount = getAdaptiveParticleCount(this.gpuTier, this.map.getZoom(), canvas.width, canvas.height);
      const delta = Math.abs(newCount - this.currentParticleCount) / this.currentParticleCount;

      if (delta > 0.2) {
        console.log(`WindParticleLayer: Adaptive resize ${this.currentParticleCount} → ${newCount} particles`);
        this.currentParticleCount = newCount;
        this.initParticleBuffers(this.gl);
      }
    }, 300);
  }

  private async loadWindData(): Promise<void> {
    if (!this.map || !this.gl || this.isLoadingData) return;
    this.isLoadingData = true;

    try {
      const mapBounds = this.map.getBounds();
      const viewportBounds: GeoBounds = {
        west: mapBounds.getWest(),
        south: mapBounds.getSouth(),
        east: mapBounds.getEast(),
        north: mapBounds.getNorth(),
      };
      const zoom = Math.floor(this.map.getZoom());
      const source = this.dataLoader.selectSource(viewportBounds, zoom);

      const metadata = await this.dataLoader.fetchMetadata(source);
      if (metadata) {
        this.currentUMin = metadata.uMin;
        this.currentUMax = metadata.uMax;
        this.currentVMin = metadata.vMin;
        this.currentVMax = metadata.vMax;
        const maxU = Math.max(Math.abs(metadata.uMin), Math.abs(metadata.uMax));
        const maxV = Math.max(Math.abs(metadata.vMin), Math.abs(metadata.vMax));
        this.currentMaxSpeed = Math.sqrt(maxU * maxU + maxV * maxV);
        this.currentMetadata = metadata;
      }

      // Use tiled loading (falls back to z0 when maxZoom=0 or tiles unavailable)
      const result = await this.dataLoader.loadWindData(source, metadata, viewportBounds, zoom);
      if (!result) {
        // null means either failure or no tile change — only warn on initial load
        if (!this.hasData) {
          console.warn(`WindParticleLayer: No wind data available from ${source.name}`);
        }
        return;
      }

      this.currentBounds = result.bounds;
      this.currentIsGlobal = !result.isTiled && source.bounds === null;
      this.currentSourceName = source.name;

      const gl = this.gl;
      if (this.windTexture) gl.deleteTexture(this.windTexture);
      this.windTexture = this.dataLoader.createWindTexture(gl, result.imageData);

      if (this.windTexture) {
        this.hasData = true;
        this.lastDataFetchTime = Date.now();
        const tileInfo = result.isTiled ? ' (tiled atlas)' : '';
        console.log(`WindParticleLayer: Loaded ${source.name} wind data (${result.imageData.width}x${result.imageData.height})${tileInfo}`);

        // Publish state to React UI (legend, freshness badge)
        useWindStore.getState().setWindState({
          sourceName: this.currentSourceName,
          maxSpeed: this.currentMaxSpeed,
          lastFetchedAt: this.lastDataFetchTime,
          hasData: true,
        });
      }
    } finally {
      this.isLoadingData = false;
    }
  }

  // ─── Private: GL Helpers ───────────────────────────────────────────

  private createColorRampTexture(gl: WebGL2RenderingContext): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) return null;
    const data = generateColorRampData();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }

  private createProgram(
    gl: WebGL2RenderingContext,
    vertSrc: string,
    fragSrc: string,
    tfVaryings?: string[],
  ): WebGLProgram | null {
    const vert = this.compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    const frag = this.compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    if (!vert || !frag) return null;

    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    if (tfVaryings) gl.transformFeedbackVaryings(program, tfVaryings, gl.INTERLEAVED_ATTRIBS);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('WindParticleLayer: Shader link error:', gl.getProgramInfoLog(program));
      gl.deleteProgram(program);
      return null;
    }

    gl.deleteShader(vert);
    gl.deleteShader(frag);
    return program;
  }

  private compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const typeName = type === gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT';
      console.error(`WindParticleLayer: ${typeName} shader compile error:`, gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private trackFPS(): void {
    const now = performance.now();
    if (this.lastFrameTime > 0) {
      const fps = 1000 / (now - this.lastFrameTime);
      this.fpsAccumulator += fps;
      this.frameCount++;
      if (this.frameCount >= 60) {
        const avgFPS = this.fpsAccumulator / this.frameCount;
        if (avgFPS < 30 && this.gpuTier !== 'low') {
          const newTier: GPUTier = this.gpuTier === 'high' ? 'medium' : 'low';
          console.warn(`WindParticleLayer: FPS=${avgFPS.toFixed(1)}, dropping to ${newTier} tier`);
          this.gpuTier = newTier;
          this.config = PARTICLE_CONFIG[newTier];
          if (this.gl && this.map) {
            const canvas = this.gl.canvas as HTMLCanvasElement;
            this.currentParticleCount = getAdaptiveParticleCount(newTier, this.map.getZoom(), canvas.width, canvas.height);
            this.initParticleBuffers(this.gl);
          }
        }
        this.frameCount = 0;
        this.fpsAccumulator = 0;
      }
    }
    this.lastFrameTime = now;
  }
}
